import React, { useContext, useEffect, useRef, useState } from "react";

import { OutroPropsExtented } from "@/common/Outro";
import LightRegistrationForm from "@/common/Outro/RegistrationForm/templates/light/LightRegistrationForm";
import { getApiUrl, getDocumentUrl } from "@/config";
import { GlobalContext } from "@/contexts";
import { useTimeout } from "@/hooks";
import IFrameService from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import {
  Participant,
  RegistrationFormInteractionOutro,
  RegistrationStatus,
} from "@/service/Model";
import { log } from "@/utils/logger";

import ClassicRegistrationForm from "./templates/classic/ClassicRegistrationForm";

const TIMEOUT_TO_RECEIVE_POST_MESSAGE_WITH_HANS_ID = 200;

export interface RegistrationFormProps extends OutroPropsExtented {
  registrationStatus?: RegistrationStatus;
  hansId?: string;
  hansIdExists?: boolean;
  onSubmit?: (payload: Record<string, string | boolean> | Participant) => void;
  onSubmitEnd?: (status: RegistrationStatus) => void;
  outro: RegistrationFormInteractionOutro;
  lightLayout: boolean;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = (
  props: RegistrationFormProps
): React.JSX.Element => {
  const {
    onSubmitEnd,
    lightLayout,
    outro: { hansIdOptIn },
  } = props;
  const [hansIdExists, setHansIdExists] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const globalContext = useContext(GlobalContext);
  const { registrationStatus, preview, hansId, handleRegistration } =
    globalContext;
  // hansId reference is needed for timeout callback
  const hansIdRef = useRef<string | undefined>(hansId);

  const api = new InteractionService(getApiUrl(preview));

  useEffect(() => {
    // Get hansId from adLib - useEffect on 'hansId' will be triggered
    IFrameService.getHansId();
  }, []);

  useEffect(() => {
    hansIdRef.current = hansId;
    if (!hansId || !hansIdOptIn) {
      // hansId is not received or checkbox setting is not configured - no query from the database necessary
      setLoading(false);
      return;
    }
    verifyConsentForHansId(hansId);
  }, [hansId]);

  useTimeout(() => {
    // - Checkbox setting is configured
    // - No hansId received
    // - Not preview mode
    if (hansIdOptIn && !hansIdRef.current && !preview) {
      // Possible reasons:
      // - The use of the Hans-ID can be rejected by the user via a cookie banner, etc.
      // - Timing problem: PostMessage sender and recipient miss each other
      log.info(
        "Registration form without hans-opt-in. Reason: No hans ID received. Referrer: " +
          getDocumentUrl()
      );
    }
  }, TIMEOUT_TO_RECEIVE_POST_MESSAGE_WITH_HANS_ID);

  const verifyConsentForHansId = (hansId: string) => {
    api
      .hansIdExists(hansId)
      .then((exists: boolean) => {
        setHansIdExists(exists);
      })
      .catch(() => {
        setHansIdExists(false);
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (
    payload: Record<string, string | boolean> | Participant
  ) =>
    handleRegistration(payload).then((status: RegistrationStatus) =>
      onSubmitEnd?.(status)
    );

  if (loading) {
    return <></>;
  }

  return (
    <div data-hans-id-received={!!hansId}>
      {lightLayout ? (
        <LightRegistrationForm
          {...props}
          hansId={hansId}
          hansIdExists={hansIdExists}
          registrationStatus={registrationStatus}
          onSubmit={handleSubmit}
        />
      ) : (
        <ClassicRegistrationForm
          {...props}
          hansId={hansId}
          hansIdExists={hansIdExists}
          registrationStatus={registrationStatus}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};
